import { Badge, Divider, Grid, Typography } from "antd";
import { Header } from "antd/es/layout/layout";
import { useOnlineClientsCountQuery, useOnlineSalesmansCountQuery } from "graphql/Chats/OnlineClientsCountQueries";
import User from "graphql/User/User";
import { DeletionRequestStatus, UserRole } from "graphql/apollo-graphql-generated/globalTypes";
import UserDropdown from "pages/User/components/UserDropdown";
import { OnlineSalesmansModal } from "./OnlineSalesmansModal";
import { useMemo, useState } from "react";
import { reportToBugsnag } from 'utils/reportToBugsnag';
import { useDeletionRequestsQuery } from "graphql/DeletionRequests/DeletionRequestQueries";
import { NavLink } from "react-router-dom";
import { ROUTES } from "pages/Root/PagesController";

type OnlineCountProps = {
  count: number
  name: { singular: string, plural: string }
  onClick?: () => void
}
const OnlineCount = (props: OnlineCountProps) => {
  const { count, name, onClick } = props;

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  return <div
    style={{ display: 'flex', gap: 4, alignItems: 'center', cursor: onClick ? 'pointer' : 'default' }}
    onClick={onClick}
  >
    <Badge count={count} color='#28B62C' overflowCount={1000} />
    <span>{`${count === 1 ? name.singular : name.plural}${screens?.md ? ' online' : ''}`}</span>
  </div>
}

type DashboardHeaderProps = {
  selectedWidgetIds: string[]
  onLogout: (redirectTo?: string) => void
  onOpenAgenciesSelection: () => void
  user: User
}
export const DashboardHeader = (props: DashboardHeaderProps) => {
  const { onLogout, selectedWidgetIds, user, onOpenAgenciesSelection } = props;
  const [onlineSalesmansModalVisible, setOnlineSalesmansModalVisible] = useState<boolean>(false);
  const isAdminOrManager = useMemo(() => !!user && (user?.role === UserRole.FIREFLY_ADMIN || user?.role === UserRole.AGENT_MANAGER || user?.role === UserRole.AGENT_DIRECTOR), [user])
  const isAdmin = useMemo(() => !!user && (user?.role === UserRole.FIREFLY_ADMIN || user?.role === UserRole.HARRY_ADMIN), [user])

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const { data: clientsData } = useOnlineClientsCountQuery({
    variables: { widgetIds: selectedWidgetIds }, pollInterval: 5 * 60 * 1000,
    onError(error) {
      reportToBugsnag({ error, user, caller: 'useOnlineClientsCountQuery' });
    },
  });
  const { data: salesmansData, refetch } = useOnlineSalesmansCountQuery({
    variables: { widgetIds: selectedWidgetIds }, pollInterval: 5 * 60 * 1000,
    skip: !isAdminOrManager,
    onError(error) {
      reportToBugsnag({ error, user, caller: 'useOnlineSalesmansCountQuery' });
    },
  });
  const { data: deletionRequestsData } = useDeletionRequestsQuery({
    variables: { status: DeletionRequestStatus.PENDING },
    pollInterval: 5 * 60 * 1000,
    skip: !isAdmin,
    onError(error) {
      reportToBugsnag({ error, user, caller: 'useDeletionRequestsQuery' });
    },
  });

  const clientsOnlineCount = clientsData?.onlineClientsCount || 0;
  const salesmansOnlineCount = salesmansData?.onlineSalesmansCount?.count || 0;
  const pendingDeletionRequestsCount = deletionRequestsData?.deletionRequests?.length || 0;

  return <>
    <Header style={{
      padding: '16px',
      background: '#fff',
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
    }}>
      {isAdmin && pendingDeletionRequestsCount > 0 ? <>
        <NavLink to={ROUTES.deletionRequests.getUrl()} style={{ textDecoration: 'none' }}>
          <div
            style={{ display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}
          >
            <Badge count={pendingDeletionRequestsCount} color='#FF4D4F' overflowCount={1000} />
            <span style={{ color: '#FF4D4F' }}>{`${pendingDeletionRequestsCount === 1 ? 'Exclusão pendente' : 'Exclusões pendentes'}`}</span>
          </div>
        </NavLink>
        <Divider type='vertical' />
      </> : null}
      {isAdminOrManager && salesmansOnlineCount > 0 ? <>
        <OnlineCount
          count={salesmansOnlineCount}
          name={{ plural: 'Corretores', singular: 'Corretor' }}
          onClick={() => setOnlineSalesmansModalVisible(true)}
        />
        <Divider type='vertical' />
      </> : null}
      {clientsOnlineCount > 0 ? <>
        <OnlineCount
          count={clientsOnlineCount}
          name={{ plural: 'Clientes', singular: 'Cliente' }}
        />
        <Divider type='vertical' />
      </> : null}
      <UserDropdown
        onLogout={onLogout}
        user={user}
        onOpenAgenciesSelection={onOpenAgenciesSelection}
      />
      <Divider type='vertical' />
      <Typography.Text type="secondary">{screens?.md ? 'Versão ' : null}{process.env.REACT_APP_VERSION}</Typography.Text>
    </Header>
    <OnlineSalesmansModal
      open={onlineSalesmansModalVisible}
      onCancel={() => setOnlineSalesmansModalVisible(false)}
      salesmans={salesmansData?.onlineSalesmansCount?.salesmans}
      refetch={refetch}
    />
  </>
}
